import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return '—';
  }
}

function formatDuration(totalSeconds) {
  const sec = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (sec < 60) return `${sec} сек`;
  const mins = Math.floor(sec / 60);
  if (mins < 60) return `${mins} мин`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours} ч ${rem} мин` : `${hours} ч`;
}

export default function TeacherNewUserActivityPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/teacher/new-users/${userId}/activity/`);
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(
        e.response?.status === 403
          ? 'Раздел доступен только администратору сайта.'
          : e.response?.status === 404
            ? 'Пользователь не найден.'
            : e.response?.data?.detail || 'Не удалось загрузить активность.',
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const user = data?.user;

  return (
    <div className="teacher-new-users-page teacher-new-user-activity-page">
      <div className="page-header">
        <p>
          <Link to="/teacher/new-users" className="back-link">← Новые пользователи</Link>
        </p>
        <h1>
          {user
            ? `Активность: ${user.display_name || user.first_name || user.username}`
            : 'Активность посетителя'}
        </h1>
        {user && (
          <p className="page-subtitle">
            @{user.username}
            {user.contact_display ? ` · ${user.contact_display}` : ''}
            {' · '}зарегистрирован {formatDateTime(user.date_joined)}
          </p>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-screen">
          <div className="spinner" />
          <p>Загрузка...</p>
        </div>
      ) : data ? (
        <>
          <p className="text-secondary teacher-activity-tracking-note">
            {data.tracking_note}
          </p>

          <div className="teacher-activity-summary-grid">
            <div className="teacher-activity-stat-card">
              <div className="teacher-activity-stat-label">Визитов</div>
              <div className="teacher-activity-stat-value">{data.visit_count ?? 0}</div>
            </div>
            <div className="teacher-activity-stat-card">
              <div className="teacher-activity-stat-label">На сайте</div>
              <div className="teacher-activity-stat-value">
                {formatDuration(data.total_duration_seconds)}
              </div>
            </div>
            <div className="teacher-activity-stat-card">
              <div className="teacher-activity-stat-label">Последний раз</div>
              <div className="teacher-activity-stat-value teacher-activity-stat-value--sm">
                {formatDateTime(data.last_visit_at)}
              </div>
            </div>
            <div className="teacher-activity-stat-card">
              <div className="teacher-activity-stat-label">Вход (login)</div>
              <div className="teacher-activity-stat-value teacher-activity-stat-value--sm">
                {formatDateTime(user?.last_login)}
              </div>
            </div>
          </div>

          <section className="teacher-activity-section">
            <h2>Что делал</h2>
            <ul className="teacher-activity-facts">
              <li>Анкета: {data.has_questionnaire ? 'заполнена' : 'нет'}</li>
              <li>
                Попытки задач: {data.attempt_count ?? 0}
                {data.is_my_student ? (
                  <>
                    {' · '}
                    <Link to={`/teacher/students/${userId}/activity`}>журнал попыток</Link>
                  </>
                ) : null}
              </li>
              <li>Сессии тренажёров: {data.trainer_session_count ?? 0}</li>
              <li>
                Ваш ученик:{' '}
                {data.is_my_student ? (
                  <Link to={`/teacher/students/${userId}`}>да</Link>
                ) : (
                  'нет'
                )}
              </li>
            </ul>
          </section>

          <section className="teacher-activity-section">
            <h2>Разделы сайта</h2>
            {(!data.areas || data.areas.length === 0) ? (
              <p className="text-secondary">Пока нет данных сердцебиения.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Раздел</th>
                    <th>Время</th>
                    <th>Заходов</th>
                  </tr>
                </thead>
                <tbody>
                  {data.areas.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td>{formatDuration(row.seconds)}</td>
                      <td>{row.hits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="teacher-activity-section">
            <h2>Последние попытки</h2>
            {(!data.recent_attempts || data.recent_attempts.length === 0) ? (
              <p className="text-secondary">Попыток пока нет.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Когда</th>
                    <th>Задача</th>
                    <th>Оценка</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_attempts.map((a) => (
                    <tr key={a.id}>
                      <td>{formatDateTime(a.created_at)}</td>
                      <td>{a.task_title}</td>
                      <td>{a.self_report || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="teacher-activity-section">
            <h2>Недавние визиты</h2>
            {(!data.recent_visits || data.recent_visits.length === 0) ? (
              <p className="text-secondary">Визитов пока нет.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Начало</th>
                    <th>До</th>
                    <th>Длительность</th>
                    <th>Разделы</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_visits.map((v) => (
                    <tr key={v.id}>
                      <td>{formatDateTime(v.started_at)}</td>
                      <td>{formatDateTime(v.last_seen_at)}</td>
                      <td>{formatDuration(v.duration_seconds)}</td>
                      <td>
                        {(v.areas || [])
                          .map((a) => a.label)
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
